import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import dentistryImage from '../../assets/images/Experience.jpeg';

const ExperienceSection = styled.section`
  padding: 80px 0;
  background-color: #f8f9fa;
  
  .experience-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  
  /* RTL desktop specific layout */
  [dir="rtl"] .experience-container {
    flex-direction: row-reverse;
  }
  
  /* RTL text alignment for desktop */
  [dir="rtl"] .content-column {
    text-align: right;
  }
  
  [dir="rtl"] .description {
    text-align: right;
  }
  
  @media (max-width: 992px) {
    padding: 50px 0;
    
    .experience-container {
      flex-direction: column;
    }
    
    /* Arabic mobile specific adjustments */
    [dir="rtl"] .experience-container {
      flex-direction: column;
      text-align: center;
    }
    
    [dir="rtl"] .description,
    [dir="rtl"] h2,
    [dir="rtl"] p {
      text-align: center;
    }
  }
  
  /* Make sure RTL content is properly spaced and centered on mobile */
  @media (max-width: 768px) {
    [dir="rtl"] .content-column {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 0 !important;
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
    }
    
    [dir="rtl"] .description {
      width: 90%;
      text-align: center !important;
      margin: 0 auto 20px;
      max-width: 500px;
    }
    
    [dir="rtl"] .experience-heading {
      width: 100%;
      text-align: center !important;
      padding: 0 10px;
    }
    
    [dir="rtl"] .experience-image {
      width: 90%;
      margin: 0 auto;
      max-width: 400px;
    }
    
    [dir="rtl"] p {
      text-align: center !important;
    }
    
    /* Additional specificity to ensure centering */
    [dir="rtl"] .experience-container p,
    [dir="rtl"] .experience-container span,
    [dir="rtl"] .experience-container div {
      text-align: center !important;
    }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ContentColumn = styled.div`
  width: 30%;
  padding-right: 20px;
  order: 1; /* Default order for desktop */
  
  @media (max-width: 992px) {
    width: 100%;
    padding-right: 0;
    margin-bottom: 30px;
    text-align: center;
    order: 2; /* Second element on mobile, after the heading */
  }
`;

const ExperienceHeading = styled.h2`
  width: 30%;
  font-size: 48px;
  line-height: 1.2;
  font-weight: bold;
  text-align: left;
  order: 3; /* Default order for desktop */
  
  .experience-text, .art-text, .of-text, .dentistry-text {
    display: block;
    color: #333;
    font-weight: bold;
  }
  
  .luxury-text {
    display: block;
    color: #3858F9;
    font-size: 72px;
    font-weight: bold;
  }
  
  @media (max-width: 992px) {
    width: 100%;
    font-size: 36px;
    text-align: center;
    margin-top: 0;
    margin-bottom: 30px;
    order: 1; /* First element on mobile */
    
    .luxury-text {
      font-size: 48px;
    }
  }
  
  /* Make sure Arabic heading is properly centered on mobile */
  @media (max-width: 768px) {
    [dir="rtl"] & {
      text-align: center;
      width: 100%;
    }
  }
`;

const Description = styled.p`
  margin-bottom: 25px;
  font-size: 16px;
  line-height: 1.8;
  color: #555;
  max-width: 500px;
`;

const ConsultationButton = styled.a`
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: #3858F9;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid #3858F9;
  padding: 12px 20px;
  border-radius: 4px;
  transition: all 0.3s;
  cursor: pointer;
  text-decoration: none;
  
  &:hover {
    background-color: #3858F9;
    color: white;
    transform: translateY(-3px);
  }
`;

// RTL version of Consultation Button with left-positioned arrow
const RtlConsultationButton = styled.a`
  position: relative;
  display: inline-block;
  background: transparent;
  color: #3858F9;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid #3858F9;
  padding: 12px 20px 12px 56px; /* Extra padding on left for arrow */
  border-radius: 4px;
  transition: all 0.3s;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    background-color: #3858F9;
    color: white;
    transform: translateY(-3px);
  }
  
  svg {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
  }

  svg {
    margin-left: 10px;
  }
`;

const ImageColumn = styled.div`
  width: 35%;
  position: relative;
  order: 2; /* Default order for desktop */
  
  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 992px) {
    width: 100%;
    max-width: 400px;
    margin: 30px auto;
    order: 3; /* Last element on mobile */
  }
`;

const SpecialistsBadge = styled.div`
  position: absolute;
  right: 20px;
  bottom: -20px;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 12px 20px;
  border-radius: 5px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  
  p {
    margin: 0;
    font-weight: bold;
    color: #333;
    text-transform: uppercase;
    font-size: 14px;
    white-space: nowrap;
  }
  
  @media (max-width: 992px) {
    right: auto;
    bottom: -15px;
  }
`;

const Experience = ({ id }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <ExperienceSection id={id} dir={isRtl ? 'rtl' : 'ltr'}>
      <Container>
        <div className="experience-container">
          <ContentColumn className="content-column" style={isRtl ? { 
              paddingRight: '0', 
              paddingLeft: '20px',
              textAlign: isMobile ? 'center' : 'right' 
            } : {}}>
            <Description style={isRtl ? { textAlign: isMobile ? 'center' : 'right', fontSize: '18px', lineHeight: '2' } : {}}>
              {t('home.experience.description1')}
            </Description>
            
            <Description style={isRtl ? { textAlign: isMobile ? 'center' : 'right', fontSize: '18px', lineHeight: '2' } : {}}>
              {t('home.experience.description2')}
            </Description>
            
            {isRtl ? (
              <RtlConsultationButton onClick={() => {
                // Open WhatsApp chat
                window.open('https://wa.me/966558507990', '_blank');
              }}>
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line x1="19" y1="12" x2="5" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="12 5 5 12 12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {t('home.experience.bookButton')}
              </RtlConsultationButton>
            ) : (
              <ConsultationButton onClick={() => {
                // Open WhatsApp chat
                window.open('https://wa.me/966558507990', '_blank');
              }}>
                {t('home.experience.bookButton')}
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '10px' }}>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </ConsultationButton>
            )}
          </ContentColumn>
          
          <ImageColumn className="experience-image">
            <img src={dentistryImage} alt="Dental Practice" />
            <SpecialistsBadge style={isRtl ? { right: 'auto', left: '20px', textAlign: 'right' } : {}}>
              <p>{t('home.experience.specialists')}</p>
            </SpecialistsBadge>
          </ImageColumn>
          
          <ExperienceHeading className="experience-heading" style={isRtl ? { textAlign: isMobile ? 'center' : 'right', fontFamily: 'Tajawal' } : {}}>
            <span className="experience-text">{t('home.experience.heading.line1')}</span>
            <span className="art-text">{t('home.experience.heading.line2')}</span>
            <span className="of-text">{t('home.experience.heading.line3')}</span>
            <span className="dentistry-text">{t('home.experience.heading.line4')}</span>
            <span className="luxury-text">{t('home.experience.heading.highlight')}</span>
          </ExperienceHeading>
        </div>
      </Container>
    </ExperienceSection>
  );
};

export default Experience;
